/**
 * Engine-guided per-street action entry — one section per reachable street.
 *
 * Action drives the reveal: a postflop section renders EDITABLE board card slots
 * (flop = 3, turn = 1, river = 1) and gates the action keypad until that
 * street's board is complete ("Place the flop to continue"). Once the board is
 * down, it asks the engine whose turn it is + the legal actions and renders ONLY
 * those buttons for that seat. Tapping appends the action and advances;
 * Bet/Raise reveal an absolute-level amount input ("raise to 9").
 *
 * Two things stay visible the whole way through:
 *   - the RECAP — every entered action, formatted ("UTG raises to 3 · Hero
 *     calls · BB folds"), open OR closed, so the user can always see what they
 *     entered (with an Undo last action affordance);
 *   - the ACTOR CUE — bold + accent-tinted directly above the keypad ("**Hero**
 *     to act · 1bb to call"), swapped for a subtle "complete" / "complete ·
 *     showdown" line when the round closes.
 *
 * No poker logic here — `nextDecision` / `streetState` do all of it. The board
 * card setters are owned by the parent (dedup + street-object lifecycle live in
 * the builder).
 */

import { useState } from "react";

import type { BuiltHand, BuiltHandAction, HandActionKind } from "@/shared/models";
import {
  actorForSeat,
  nextDecision,
  sizingPresets,
  streetState,
  type SizingPreset,
  type StreetKey,
} from "../lib/handTableEngine";
import { CardSlot } from "./CardSlot";

const ACTION_VERB: Record<HandActionKind, string> = {
  fold: "Fold",
  check: "Check",
  call: "Call",
  bet: "Bet",
  raise: "Raise",
};

const NEEDS_AMOUNT = new Set<HandActionKind>(["bet", "raise"]);

/** Editable board slots for a postflop street: token + a setter per index. */
export interface StreetBoardSlot {
  token: string | null;
  /** Cards already placed elsewhere (disabled in the picker). */
  disabled: Set<string>;
  onPick: (token: string) => void;
  onClear: () => void;
}

export function StreetActions({
  hand,
  street,
  label,
  boardSlots,
  boardComplete,
  onAppend,
  onUndo,
}: {
  hand: BuiltHand;
  street: StreetKey;
  label: string;
  /** Editable board card slots for this street (empty for preflop). */
  boardSlots: StreetBoardSlot[];
  /** Whether this street's board is fully placed (keypad gated until true). */
  boardComplete: boolean;
  onAppend: (action: HandActionKind, amount?: number) => void;
  onUndo: () => void;
}) {
  const decision = boardComplete ? nextDecision(hand, street) : null;
  const state = streetState(hand, street);
  const actions = hand.streets[street]?.actions ?? [];
  const actionCount = actions.length;

  // Amount input is per-pending-decision; reset when the seat/level changes by
  // keying the input on the decision identity.
  const decisionKey = decision
    ? `${decision.seat}-${state.level}-${actionCount}`
    : "closed";

  return (
    <section className="rounded-xl border border-border bg-bg/40 p-3">
      {/* Header: street label + its board cards (editable for postflop). */}
      <div className="mb-2 flex items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-accent">
          {label}
        </span>
        {boardSlots.length > 0 && (
          <span className="flex gap-1">
            {boardSlots.map((slot, i) => (
              <CardSlot
                key={i}
                token={slot.token}
                size="sm"
                pickerTitle="Board card"
                disabled={slot.disabled}
                onPick={slot.onPick}
                onClear={slot.onClear}
                ariaLabel={`${label} board card ${i + 1}`}
              />
            ))}
          </span>
        )}
      </div>

      {/* Recap: every entered action, always visible (open or closed). */}
      {actionCount > 0 && <ActionRecap hand={hand} actions={actions} />}

      {/* Gate the keypad until the board is placed. */}
      {!boardComplete ? (
        <p className="text-sm text-text-muted">
          Place the {label.toLowerCase()} to continue.
        </p>
      ) : !decision ? (
        <p className="text-xs text-text-muted">
          {state.remaining.length >= 2 ? "complete · showdown" : "complete"}
        </p>
      ) : (
        <>
          {/* Actor cue — bold + accent, directly above the keypad. */}
          <p className="mb-2 text-sm text-text-muted">
            <span className="font-bold text-accent">
              {actorForSeat(hand, decision.seat)}
            </span>{" "}
            to act
            {decision.toCall > 0 && <> · {decision.toCall}bb to call</>}
          </p>
          <ActionKeypad
            key={decisionKey}
            actions={decision.actions}
            toCall={decision.toCall}
            level={state.level}
            presets={sizingPresets(hand, street)}
            onAppend={onAppend}
          />
        </>
      )}

      {actionCount > 0 && (
        <div className="mt-2">
          <button
            type="button"
            onClick={onUndo}
            className="text-xs text-text-muted underline-offset-2 hover:text-text hover:underline"
          >
            Undo last action
          </button>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// ActionRecap — the always-visible list of entered actions for a street.
// ---------------------------------------------------------------------------

/** Format one action as a human phrase ("raises to 3", "calls", "folds"). */
function describeAction(a: BuiltHandAction): string {
  switch (a.action) {
    case "raise":
      return `raises to ${a.amount ?? "?"}`;
    case "bet":
      return `bets ${a.amount ?? "?"}`;
    case "call":
      return a.amount && a.amount > 0 ? `calls ${a.amount}` : "calls";
    case "check":
      return "checks";
    case "fold":
      return "folds";
  }
}

function ActionRecap({
  hand,
  actions,
}: {
  hand: BuiltHand;
  actions: BuiltHandAction[];
}) {
  return (
    <p className="mb-2 text-sm leading-relaxed text-text-muted">
      {actions.map((a, i) => (
        <span key={i}>
          {i > 0 && <span className="text-text-muted/50"> · </span>}
          <span className="font-medium text-text">
            {actorForSeat(hand, a.actor)}
          </span>{" "}
          {describeAction(a)}
        </span>
      ))}
    </p>
  );
}

// ---------------------------------------------------------------------------
// ActionKeypad — the legal-action buttons for the seat to act. Bet/Raise reveal
// an absolute-level amount input before committing.
// ---------------------------------------------------------------------------

function ActionKeypad({
  actions,
  toCall,
  level,
  presets,
  onAppend,
}: {
  actions: HandActionKind[];
  toCall: number;
  level: number;
  /** Context-aware sizing presets for a bet/raise (preflop multiples,
   *  postflop %-of-pot) — one-tap commit, mirroring the drill SpotCard. */
  presets: SizingPreset[];
  onAppend: (action: HandActionKind, amount?: number) => void;
}) {
  const [pending, setPending] = useState<HandActionKind | null>(null);
  // Default the raise/bet input to a sensible level: a min open of 2.5bb
  // preflop-ish, or just above the current level so it's always a legal raise.
  const [amount, setAmount] = useState<string>("");

  function commit(action: HandActionKind) {
    if (NEEDS_AMOUNT.has(action)) {
      setPending(action);
      setAmount(amount || suggestLevel(action, level));
      return;
    }
    // Call stores the absolute level it matches (for display); fold/check
    // carry no amount.
    onAppend(action, action === "call" ? level : undefined);
  }

  function confirmAmount() {
    const n = parseFloat(amount);
    if (!(n > 0) || !pending) return;
    onAppend(pending, n);
    setPending(null);
    setAmount("");
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {actions.map((a) => (
          <button
            key={a}
            type="button"
            onClick={() => commit(a)}
            className={[
              "rounded-lg border px-3.5 py-2 text-sm font-medium transition",
              a === "fold"
                ? "border-border text-text-muted hover:border-danger/50 hover:text-danger"
                : pending === a
                  ? "border-accent bg-accent/15 text-text"
                  : "border-accent/40 bg-accent/10 text-text hover:bg-accent/15",
            ].join(" ")}
          >
            {ACTION_VERB[a]}
            {a === "call" && toCall > 0 ? ` ${toCall}` : ""}
          </button>
        ))}
      </div>

      {pending && (
        <div className="space-y-2">
          {/* One-tap sizing presets (preflop multiples / postflop %-of-pot). */}
          {presets.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {presets.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => {
                    onAppend(pending, p.amount);
                    setPending(null);
                    setAmount("");
                  }}
                  className="flex flex-col items-center rounded-lg border border-border bg-white/[0.02] px-3 py-1.5 transition hover:border-accent hover:bg-accent/10 active:scale-[0.98]"
                >
                  <span className="text-sm font-medium text-text">{p.label}</span>
                  <span className="text-[11px] text-text-muted">{p.amount}bb</span>
                </button>
              ))}
            </div>
          )}
          {/* Custom amount. */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-text-muted">
              {ACTION_VERB[pending]} to
            </span>
            <input
            type="number"
            inputMode="decimal"
            min={level || 0.5}
            step="0.5"
            autoFocus
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && confirmAmount()}
            aria-label="Bet level in big blinds"
            className="w-20 rounded-lg border border-border bg-bg px-2.5 py-1.5 text-center text-text focus:border-accent/50 focus:outline-none"
          />
          <span className="text-sm text-text-muted">bb</span>
          <button
            type="button"
            onClick={confirmAmount}
            className="rounded-lg border border-accent/40 bg-accent/10 px-3 py-1.5 text-sm font-medium text-text hover:bg-accent/15"
          >
            Confirm
          </button>
            <button
              type="button"
              onClick={() => setPending(null)}
              className="rounded-lg px-2 py-1.5 text-sm text-text-muted hover:text-text"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** A sensible default bet level just above the current bet (legal raise). */
function suggestLevel(action: HandActionKind, level: number): string {
  if (action === "bet") return level > 0 ? String(level * 2) : "2.5";
  // raise: min raise to ~2.5x the level (clean default the user can edit)
  return String(level > 0 ? Math.round(level * 2.5 * 10) / 10 : 2.5);
}
